import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Search, Lock, Play, History, ArrowLeft } from "lucide-react";
import { useFilms } from "../context/FilmsContext";
import { InnerNav } from "../components/landing/InnerNav";
import { useAuth } from "../context/AuthContext";
import { fetchProgress } from "../lib/progress";

const GENRES = ["All", "Horror", "Sci-Fi", "Comedy"];
const GENRE_ORDER = ["Horror", "Sci-Fi", "Comedy"];

const GENRE_META = {
  Horror: "Vampires, zombies, mad doctors & painted shadows",
  "Sci-Fi": "Rockets, robots, ray-guns & other worlds",
  Comedy: "Stooges, slapstick, silent clowns & screwball chaos",
};

const FilmCard = ({ f, index, subscribed }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-40px" }}
    transition={{ duration: 0.45, delay: (index % 8) * 0.04 }}
  >
    <Link
      to={`/watch/${f.id}`}
      data-testid={`archive-card-${f.id}`}
      className="group block relative overflow-hidden border border-white/10 hover:border-blood/60 transition-colors duration-500 aspect-[2/3] bg-secondary"
    >
      <img
        src={f.poster}
        alt={f.title}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute top-3 right-3">
        {subscribed ? (
          <span className="h-8 w-8 rounded-full bg-blood/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <Play className="h-4 w-4 fill-white text-white" />
          </span>
        ) : (
          <span className="h-8 w-8 rounded-full bg-black/70 border border-white/20 flex items-center justify-center">
            <Lock className="h-3.5 w-3.5 text-bone/80" />
          </span>
        )}
      </div>
      <div className="absolute bottom-0 p-4">
        <span className="font-mono text-[9px] tracking-[0.25em] uppercase text-blood">
          {f.category} · {f.year}
        </span>
        <h3 className="mt-1 font-serif text-xl leading-tight tracking-tight text-bone">
          {f.title}
        </h3>
      </div>
    </Link>
  </motion.div>
);

export default function Archive() {
  const { user } = useAuth();
  const { films } = useFilms();
  const subscribed = !!(user && user.subscribed);
  const [searchParams] = useSearchParams();
  const initialGenre = GENRES.includes(searchParams.get("genre"))
    ? searchParams.get("genre")
    : "All";
  const [genre, setGenre] = useState(initialGenre);
  const [query, setQuery] = useState("");
  const [continueIds, setContinueIds] = useState([]);

  useEffect(() => {
    const g = searchParams.get("genre");
    if (GENRES.includes(g)) setGenre(g);
  }, [searchParams]);

  const cwTimer = useRef();
  const loadProgress = useCallback(() => {
    clearTimeout(cwTimer.current);
    cwTimer.current = setTimeout(() => {
      if (user) fetchProgress().then(setContinueIds);
      else setContinueIds([]);
    }, 200);
  }, [user]);

  useEffect(() => {
    loadProgress();
  }, [loadProgress]);

  // Refresh Continue Watching when returning to the app/tab (e.g. after watching
  // a film, or reopening the installed PWA) so it never shows a stale list.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") loadProgress();
    };
    window.addEventListener("focus", loadProgress);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("pageshow", loadProgress);
    return () => {
      window.removeEventListener("focus", loadProgress);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("pageshow", loadProgress);
    };
  }, [loadProgress]);

  const continueFilms = continueIds
    .map((id) => films.find((f) => f.id === id))
    .filter(Boolean);
  const showContinue = continueFilms.length > 0 && genre === "All" && !query.trim();

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return films.filter(
      (f) =>
        !q ||
        f.title.toLowerCase().includes(q) ||
        (f.director || "").toLowerCase().includes(q) ||
        (f.blurb || "").toLowerCase().includes(q)
    );
  }, [query, films]);

  const groups = useMemo(() => {
    const shown = genre === "All" ? GENRE_ORDER : [genre];
    return shown
      .map((g) => ({ genre: g, films: matches.filter((f) => f.category === g) }))
      .filter((grp) => grp.films.length > 0);
  }, [matches, genre]);

  const total = matches.length;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <InnerNav active="archive" />

      <div className="max-w-[1600px] mx-auto px-5 md:px-10 pt-16 md:pt-24 pb-24">
        <Link
          to="/"
          data-testid="archive-back"
          className="flex w-fit items-center gap-2 mb-8 font-mono text-[11px] tracking-[0.25em] uppercase text-muted-foreground hover:text-bone transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Home
        </Link>
        <span className="font-mono text-[11px] tracking-[0.4em] uppercase text-blood">
          (The Full Vault · {films.length} Titles)
        </span>
        <h1 className="mt-4 font-serif text-6xl md:text-8xl leading-[0.85] tracking-tighter text-bone">
          The <span className="italic">Archive</span>
        </h1>

        {!subscribed && (
          <p className="mt-5 font-mono text-[11px] tracking-[0.2em] uppercase text-muted-foreground">
            {user
              ? "You're signed in — subscribe to unlock every title."
              : "Browse freely. Sign in & subscribe to press play."}
          </p>
        )}

        {/* Controls */}
        <div className="mt-10 flex flex-col md:flex-row md:items-center gap-6 md:justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            {GENRES.map((g) => (
              <button
                key={g}
                data-testid={`filter-${g.toLowerCase().replace("-", "")}`}
                onClick={() => setGenre(g)}
                className={`font-mono text-[11px] tracking-[0.2em] uppercase px-4 py-2 border transition-colors ${
                  genre === g
                    ? "border-blood bg-blood text-white"
                    : "border-white/15 text-muted-foreground hover:text-bone hover:border-white/40"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="relative w-full md:w-80">
            <Search className="absolute left-1 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <input
              data-testid="archive-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search titles, directors…"
              className="w-full bg-transparent border-0 border-b-2 border-white/20 focus:border-blood outline-none py-2.5 pl-7 pr-1 font-mono text-sm tracking-[0.1em] text-bone placeholder:text-muted-foreground/60 transition-colors"
            />
          </div>
        </div>

        {/* Continue watching */}
        {showContinue && (
          <section data-testid="continue-watching" className="mt-14">
            <div className="flex items-center gap-3 border-b border-white/12 pb-4">
              <History className="h-5 w-5 text-blood" />
              <h2 className="font-serif text-3xl md:text-4xl tracking-tighter text-bone">
                Continue watching
              </h2>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {continueFilms.map((f, i) => (
                <FilmCard key={`cw-${f.id}`} f={f} index={i} subscribed={subscribed} />
              ))}
            </div>
          </section>
        )}

        {/* Grouped sections */}
        {groups.map((grp) => (
          <section key={grp.genre} data-testid={`genre-group-${grp.genre.toLowerCase().replace("-", "")}`} className="mt-14">
            <div className="flex items-baseline justify-between border-b border-white/12 pb-4">
              <h2 className="font-serif text-4xl md:text-5xl tracking-tighter text-bone">
                {grp.genre}
              </h2>
              <span className="hidden sm:inline font-mono text-[10px] tracking-[0.25em] uppercase text-muted-foreground">
                {GENRE_META[grp.genre]} · {grp.films.length}
              </span>
            </div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {grp.films.map((f, i) => (
                <FilmCard key={f.id} f={f} index={i} subscribed={subscribed} />
              ))}
            </div>
          </section>
        ))}

        {total === 0 && (
          <p className="mt-16 text-center font-mono text-sm tracking-[0.2em] uppercase text-muted-foreground">
            No titles match your search.
          </p>
        )}
      </div>
    </div>
  );
}
